import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_FOCUS,
  type VizCue,
} from '../vizShared';

// IntermolecularForces — compare the three principal IMFs side-by-side with an
// animated particle model. Pick a force type; particles (idealised polar /
// nonpolar molecules) drift and, when close, snap toward each other along
// dashed attraction lines whose strength reflects the chosen force. A relative
// strength meter and a worked example accompany each. Resilient default:
// hydrogen bonding.
//
// Self-demo: on mount the widget cycles dispersion → dipole → hydrogen so the
// learner sees the three IMFs strengthen in order, then settles + "Now you try".
//
// cue: { step? / highlight? → select an IMF type; play? → run/pause the
//        attraction animation; progress? → also selects an IMF (0..1) }
//
// config: { force?, title? }   force ∈ 'hydrogen' | 'dipole' | 'london'

type Force = 'hydrogen' | 'dipole' | 'london';

export interface IntermolecularForcesConfig {
  title?: string;
  force?: Force;
}

interface Props {
  config?: IntermolecularForcesConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

interface ForceInfo {
  name: string;
  short: string;
  strength: number; // relative 0-1 for the meter and attraction pull
  example: string;
  blurb: string;
}

const FORCES: Record<Force, ForceInfo> = {
  hydrogen: {
    name: 'Hydrogen bonding',
    short: 'H-bond',
    strength: 1.0,
    example: 'H₂O · NH₃ · HF',
    blurb: 'H bonded to N, O, or F is strongly attracted to a lone pair on a neighbouring N/O/F. The strongest IMF.',
  },
  dipole: {
    name: 'Dipole–dipole',
    short: 'Dipole',
    strength: 0.6,
    example: 'HCl · acetone',
    blurb: 'Permanent dipoles align δ+ to δ−. Present in all polar molecules.',
  },
  london: {
    name: 'London dispersion',
    short: 'Dispersion',
    strength: 0.3,
    example: 'Cl₂ · CH₄ · Ar',
    blurb: 'Instantaneous induced dipoles from fluctuating electron clouds. The only IMF in nonpolar species; grows with size.',
  },
};

// Self-demo / cue.step order: weakest → strongest.
const ORDER: Force[] = ['london', 'dipole', 'hydrogen'];

const W = 340;
const H = 200;
const N = 6; // molecules
const R = 14;

interface Mol {
  x: number; y: number; vx: number; vy: number;
  angle: number; // dipole orientation
}

function rand(min: number, max: number) { return min + Math.random() * (max - min); }

function makeMols(): Mol[] {
  return Array.from({ length: N }, () => ({
    x: rand(30, W - 30),
    y: rand(30, H - 30),
    vx: rand(-12, 12),
    vy: rand(-12, 12),
    angle: rand(0, Math.PI * 2),
  }));
}

export default function IntermolecularForces({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const [force, setForce] = useState<Force>(config.force ?? 'hydrogen');
  // Animation runs unless paused (or reduced motion: frozen, attractions resolved).
  const [paused, setPaused] = useState(reduced);
  const [demoDone, setDemoDone] = useState(reduced || hasCue);
  const interactedRef = useRef(false);
  const demoTimers = useRef<number[]>([]);

  const info = FORCES[force];

  const mols = useMemo(() => makeMols(), []);
  const molsRef = useRef<Mol[]>(mols);
  molsRef.current = mols;
  const [, force_] = useState(0);
  // Attraction links computed each frame for rendering.
  const linksRef = useRef<Array<{ a: number; b: number; t: number }>>([]);

  const cancelDemo = () => {
    demoTimers.current.forEach((t) => window.clearTimeout(t));
    demoTimers.current = [];
    setDemoDone(true);
  };

  const markInteracted = () => {
    cancelDemo();
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'imf', force });
    }
  };

  // Self-demo: cycle through the three IMFs (weakest → strongest) on mount.
  useEffect(() => {
    if (reduced || hasCue) {
      setDemoDone(true);
      return;
    }
    setForce(ORDER[0]);
    const t1 = window.setTimeout(() => setForce(ORDER[1]), 1100);
    const t2 = window.setTimeout(() => setForce(ORDER[2]), 2200);
    const t3 = window.setTimeout(() => setDemoDone(true), 3200);
    demoTimers.current = [t1, t2, t3];
    return () => demoTimers.current.forEach((t) => window.clearTimeout(t));
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Cue handling (supersedes the self-demo) -----
  const highlights = useMemo(() => cueHighlights(cue), [cue]);
  // cue.highlight → select by force key, short name, or full name.
  useEffect(() => {
    if (highlights.length === 0) return;
    const match = (Object.keys(FORCES) as Force[]).find(
      (f) =>
        highlights.includes(f) ||
        highlights.includes(FORCES[f].short.toLowerCase()) ||
        highlights.includes(FORCES[f].name.toLowerCase()),
    );
    if (match) setForce(match);
  }, [highlights]);
  // cue.step → index into ORDER (weakest → strongest).
  useEffect(() => {
    if (cue?.step == null) return;
    const i = ((Math.round(cue.step) % ORDER.length) + ORDER.length) % ORDER.length;
    setForce(ORDER[i]);
  }, [cue?.step]);
  // cue.progress (0..1) → also picks an IMF.
  useEffect(() => {
    if (cue?.progress == null) return;
    const i = Math.min(ORDER.length - 1, Math.floor(Math.max(0, Math.min(1, cue.progress)) * ORDER.length));
    setForce(ORDER[i]);
  }, [cue?.progress]);
  // cue.play → run / pause the animation.
  useEffect(() => {
    if (cue?.play == null) return;
    setPaused(!cue.play);
  }, [cue?.play]);

  useAnimationFrame((dt) => {
    const ms = molsRef.current;
    const pull = info.strength;
    const links: Array<{ a: number; b: number; t: number }> = [];
    // Drift + wall bounce.
    for (const m of ms) {
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      if (m.x < R) { m.x = R; m.vx = Math.abs(m.vx); }
      if (m.x > W - R) { m.x = W - R; m.vx = -Math.abs(m.vx); }
      if (m.y < R) { m.y = R; m.vy = Math.abs(m.vy); }
      if (m.y > H - R) { m.y = H - R; m.vy = -Math.abs(m.vy); }
      m.angle += dt * 0.4;
    }
    // Pairwise attraction within a radius; closer + stronger force = bigger pull.
    const RANGE = 70;
    for (let i = 0; i < ms.length; i++) {
      for (let j = i + 1; j < ms.length; j++) {
        const dx = ms[j].x - ms[i].x;
        const dy = ms[j].y - ms[i].y;
        const d = Math.hypot(dx, dy);
        if (d < RANGE && d > R * 2) {
          const f = (1 - d / RANGE) * pull * 18 * dt;
          const ux = dx / d, uy = dy / d;
          ms[i].vx += ux * f; ms[i].vy += uy * f;
          ms[j].vx -= ux * f; ms[j].vy -= uy * f;
          links.push({ a: i, b: j, t: 1 - d / RANGE });
        }
      }
    }
    // Light damping so they don't runaway-accelerate.
    for (const m of ms) { m.vx *= 0.995; m.vy *= 0.995; }
    linksRef.current = links;
    force_((n) => (n + 1) % 1000);
  }, !paused && !reduced);

  const changeForce = (f: Force) => { setForce(f); markInteracted(); };

  const togglePause = () => { markInteracted(); setPaused((p) => !p); };

  return (
    <VizCard
      eyebrow="Bonding · Intermolecular forces"
      title={config.title ?? 'Intermolecular forces (IMFs)'}
      subtitle="The attractions between molecules — weakest to strongest"
      reducedMotion={reduced}
      hint="Pick a force · watch molecules attract · play/pause the model · cycles all three on load"
    >
      <div className="flex w-full flex-col gap-3">
        {/* Force selector */}
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Intermolecular force type">
          {(Object.keys(FORCES) as Force[]).map((f) => {
            const active = force === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => changeForce(f)}
                aria-pressed={active}
                className={`flex-1 ${VIZ_BTN_GHOST} justify-center ${active ? '!bg-ws-accent !text-ws-on-accent !border-ws-accent' : ''}`}
              >
                {FORCES[f].name}
              </button>
            );
          })}
        </div>

        <div className="flex flex-col gap-3 lg:flex-row">
          <div className="relative flex-shrink-0">
            <NowYouTry show={demoDone && !hasCue && !reduced} />
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className="w-full max-w-[340px] rounded-md border border-ws-border bg-ws-surface-2"
              role="img"
              aria-label={`Intermolecular forces: ${info.name}, relative strength ${Math.round(info.strength * 100)} percent`}
            >
              {/* Attraction links */}
              {linksRef.current.map((lk, i) => {
                const a = molsRef.current[lk.a];
                const b = molsRef.current[lk.b];
                if (!a || !b) return null;
                return (
                  <line
                    key={i}
                    x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                    stroke="var(--ws-accent)"
                    strokeWidth={1 + lk.t * 2}
                    strokeDasharray="3 3"
                    opacity={0.25 + lk.t * 0.6 * info.strength}
                  />
                );
              })}

              {/* Molecules: polar shown with δ+/δ− poles, nonpolar as plain blobs */}
              {molsRef.current.map((m, i) => {
                const polar = force !== 'london';
                const px = Math.cos(m.angle) * (R - 4);
                const py = Math.sin(m.angle) * (R - 4);
                return (
                  <g key={i}>
                    <circle cx={m.x} cy={m.y} r={R} fill="var(--ws-tile)" stroke="var(--ws-border-strong)" strokeWidth="1" />
                    {polar ? (
                      <>
                        <circle cx={m.x - px} cy={m.y - py} r="5" fill="var(--ws-danger)" />
                        <circle cx={m.x + px} cy={m.y + py} r="5" fill="var(--ws-accent)" />
                        <text x={m.x - px} y={m.y - py + 2.5} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#fff">−</text>
                        <text x={m.x + px} y={m.y + py + 3} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#fff">+</text>
                      </>
                    ) : (
                      <circle cx={m.x} cy={m.y} r={R - 5} fill="var(--ws-ink-4)" opacity="0.5" />
                    )}
                  </g>
                );
              })}
            </svg>
            {/* Pole legend (only meaningful for polar IMFs) */}
            <div className="mt-1 flex items-center gap-3 text-[10px] text-ws-ink-3">
              {force !== 'london' ? (
                <>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'var(--ws-danger)' }} /> δ−
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: 'var(--ws-accent)' }} /> δ+
                  </span>
                  <span>dashed = attraction</span>
                </>
              ) : (
                <span>Nonpolar molecules · transient induced dipoles · dashed = attraction</span>
              )}
            </div>
          </div>

          {/* Readout */}
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-ws-ink">{info.name}</span>
            </div>

            {/* Relative strength meter */}
            <div>
              <div className="mb-1 flex justify-between text-[11px] text-ws-ink-3">
                <span>Relative strength</span>
                <span className="font-mono">{Math.round(info.strength * 100)}%</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-ws-border">
                <div
                  className="h-full rounded-full bg-ws-accent"
                  style={{
                    width: `${info.strength * 100}%`,
                    transition: reduced ? undefined : 'width 400ms ease-out',
                  }}
                />
              </div>
            </div>

            <p className="text-xs leading-snug text-ws-ink-2">{info.blurb}</p>
            <p className="text-xs text-ws-ink-3"><span className="text-ws-ink-3">Examples: </span>{info.example}</p>

            <button
              type="button"
              onClick={togglePause}
              aria-pressed={!paused}
              disabled={reduced}
              className={`mt-1 w-fit ${VIZ_BTN_GHOST}`}
            >
              {paused ? '▶ Play' : '❚❚ Pause'}
            </button>
          </div>
        </div>
      </div>
    </VizCard>
  );
}
